import { useId } from 'react';
import { cn } from '~/lib/utils';
import styles from './DeckGooeyLogo.module.css';

const LETTERS = ['D', 'e', 'c', 'k'] as const;
const WAVE_DURATION_S = 2;
const WAVE_STAGGER_S = 0.2;
const BLUR_MIN = 0.2;
const BLUR_MAX = 2;

export interface DeckGooeyLogoProps {
  className?: string;
  ariaLabel?: string;
  /**
   * Multiplies the gooey blur. stdDeviation is in user-space px, so a logo rendered at 60px gets a
   * visibly thinner goo than the same filter at the stock 26px — scale this with the font size to
   * keep the letters equally fat.
   */
  blurScale?: number;
}

export const DeckGooeyLogo = ({
  className,
  ariaLabel = 'Deck',
  blurScale = 1,
}: DeckGooeyLogoProps) => {
  const filterBaseId = `deck-gooey-${useId().replace(/:/g, '')}`;
  const blurValues = `${BLUR_MIN * blurScale};${BLUR_MAX * blurScale};${BLUR_MIN * blurScale}`;

  return (
    <span className={cn(styles.root, className)} role="img" aria-label={ariaLabel}>
      <svg className={styles.filterSvg} aria-hidden="true" focusable="false">
        <defs>
          {LETTERS.map((_, index) => (
            <filter key={index} id={`${filterBaseId}-${index}`}>
              <feGaussianBlur in="SourceGraphic" stdDeviation="0" result="blur">
                <animate
                  attributeName="stdDeviation"
                  values={blurValues}
                  dur={`${WAVE_DURATION_S}s`}
                  repeatCount="indefinite"
                  begin={`${index * WAVE_STAGGER_S}s`}
                  calcMode="spline"
                  keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                  keyTimes="0;0.5;1"
                />
              </feGaussianBlur>
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 28 -8"
                result="gooey"
              />
              <feComposite in="SourceGraphic" in2="gooey" operator="atop" />
            </filter>
          ))}
        </defs>
      </svg>

      <span className={styles.letters} aria-hidden="true">
        {LETTERS.map((letter, index) => (
          <span
            key={letter}
            className={styles.letter}
            style={{ filter: `url(#${filterBaseId}-${index})` }}
          >
            {letter}
          </span>
        ))}
      </span>
    </span>
  );
};

DeckGooeyLogo.displayName = 'DeckGooeyLogo';
